import { useRef } from "react";
import useInView from "../../hooks/useInView";
import { ReactComponent as DownloadIcon } from "../../assets/icons/downloadIcon.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/eMailIcon.svg";
import styles from "./About.module.scss";
import AboutMeImg from "../../assets/images/aboutMe.jpeg";
import highlights from "../../api/highlights.json";
import traits from "../../api/traits.json";
import funFacts from "../../api/funFacts.json";
import MyResume from "../../assets/files/Sreerag_V_S_CV.pdf";

export default function About() {
  const sectionRef = useRef(null);
  const animate = useInView(sectionRef, 0.06);

  const fadeUp = (delay = 0) => ({
    opacity: animate ? 1 : 0,
    transform: animate ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
  });

  return (
    <>
      <section id="about" className={styles["ab-section"]} ref={sectionRef}>
        {/* ── header ── */}
        <div className={styles["ab-header"]} style={fadeUp(0)}>
          <h2>About Me</h2>
          <div className={styles["ab-underline"]} />
        </div>

        {/* ── main layout ── */}
        <div className={styles["ab-layout"]}>
          {/*left */}
          <div className={styles["ab-left"]}>
            {/* photo card */}
            <div className={styles["ab-photo-card"]} style={fadeUp(0.05)}>
              <div className={styles["aboutMeDiv"]}>
                <img
                  src={AboutMeImg}
                  alt="Sreerag V S"
                  className={styles["aboutMeImg"]}
                />
              </div>
              <div className={styles["ab-photo-info"]}>
                <div className={styles["ab-photo-name"]}>Sreerag V S</div>
                <div className={styles["ab-photo-role"]}>
                  Full Stack Engineer
                </div>
                <div className={styles["ab-photo-tags"]}>
                  {[
                    "React",
                    "Node.js",
                    "Android",
                    "Python",
                    "Angular",
                    "Laravel/ Lumen",
                    "Java",
                    "AWS",
                  ].map((t) => (
                    <span key={t} className={styles["ab-photo-tag"]}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* availability */}
            <div className={styles["ab-avail"]} style={fadeUp(0.12)}>
              <span className={styles["ab-avail-dot"]} />
              <span>Available for new projects</span>
              <span className={styles["ab-avail-sub"]}>Kerala, IN 🇮🇳</span>
            </div>

            {/* fun facts */}
            <div className={styles["ab-facts-card"]} style={fadeUp(0.18)}>
              <div className={styles["ab-facts-title"]}>Quick Facts</div>
              <div className={styles["ab-facts-list"]}>
                {funFacts.map((f) => (
                  <div key={f.text} className={styles["ab-fact-row"]}>
                    <span className={styles["ab-fact-emoji"]}>{f.emoji}</span>
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* right */}
          <div className={styles["ab-right"]}>
            {/* intro card */}
            <div className={styles["ab-intro-card"]} style={fadeUp(0.1)}>
              <div className={styles["ab-intro-eyebrow"]}>My Story</div>
              <h3>
                Building <em>products people love</em>,<br />
                one commit at a time.
              </h3>
              <p>
                I'm a{" "}
                <span className={styles["ab-highlight"]}>
                  Full Stack Software Engineer
                </span>{" "}
                with <span className={styles["ab-highlight"]}>5+ years</span> of
                experience turning ideas into production grade web and mobile
                applications. I specialise in Mobile development, full stack
                engineering, and cloud architectures — building systems that are
                fast, scalable, and maintainable.
              </p>
              <p>
                From requirement analysis all the way through to deployment and
                post launch maintenance, I'm comfortable owning the full
                development lifecycle. I've delivered solutions for startups,
                agencies, and enterprise clients — always with a sharp focus on
                code quality, user experience, and measurable outcomes.
              </p>
              <p>
                Strong problem solving instincts and a collaborative mindset
                mean I thrive in cross functional teams as much as working
                independently. When I'm not shipping features, I'm exploring new
                tools, experimenting with emerging technologies, or reading the
                latest in system design and cloud architecture.
              </p>

              <div className={styles["ab-cta-row"]}>
                <a
                  href={MyResume}
                  download="Sreerag_V_S_CV.pdf"
                  className={styles["ab-btn-dark"]}
                >
                  <DownloadIcon />
                  <span>Download CV</span>
                </a>
                <a href="#contact" className={styles["ab-btn-outline"]}>
                  <MailIcon /> Let's Talk
                </a>
              </div>
            </div>

            {/* traits grid */}
            <div className={styles["ab-traits-grid"]}>
              {traits.map((t, i) => (
                <div
                  key={t.label}
                  className={styles["ab-trait"]}
                  style={fadeUp(0.22 + i * 0.08)}
                >
                  <span className={styles["ab-trait-icon"]}>{t.icon}</span>
                  <div>
                    <div className={styles["ab-trait-label"]}>{t.label}</div>
                    <div className={styles["ab-trait-desc"]}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── bottom stat pills ── */}
        <div className={styles["ab-stats-row"]}>
          {highlights.map((h, i) => (
            <div
              key={h.label}
              className={styles["ab-mini-stat"]}
              style={fadeUp(0.45 + i * 0.08)}
            >
              <span className={styles["ab-mini-icon"]}>{h.icon}</span>
              <div className={styles["ab-mini-val"]} style={{ color: h.color }}>
                {h.value}
              </div>
              <div className={styles["ab-mini-label"]}>{h.label}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
